<template>
  <form ref="form" v-editable="blok" class="my-4 w-full">
    <div v-if="!isFinland" class="mb-6">
      <SalutationSelect
        v-model="formState.salutation"
        input-id="catalog_order_form_salutation"
        data-test-id="salutation_select"
      />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <ValidatedInputGroupLegacy
        v-slot="{ inputId }"
        :is-valid="isValidFirstName"
        :message="t('catalog_order_form.first_name.error')"
      >
        <TextInput
          v-model="formState.firstName"
          :appearance="isValidFirstName ? 'default' : 'danger'"
          :label="t('catalog_order_form.first_name.label')"
          :has-error-message="!isValidFirstName"
          :input-id="inputId"
          :required="true"
          data-test-id="first_name"
          @change="v.firstName.$touch()"
          @blur="v.firstName.$touch()"
        />
      </ValidatedInputGroupLegacy>

      <ValidatedInputGroupLegacy
        v-slot="{ inputId }"
        :is-valid="isValidLastName"
        :message="t('catalog_order_form.last_name.error')"
      >
        <TextInput
          v-model="formState.lastName"
          :appearance="isValidLastName ? 'default' : 'danger'"
          :label="t('catalog_order_form.last_name.label')"
          :has-error-message="!isValidLastName"
          :input-id="inputId"
          :required="true"
          data-test-id="last_name"
          @change="v.lastName.$touch()"
          @blur="v.lastName.$touch()"
        />
      </ValidatedInputGroupLegacy>
    </div>

    <div class="grid grid-cols-2 gap-4">
      <ValidatedInputGroupLegacy
        v-slot="{ inputId }"
        :is-valid="isValidStreet"
        :message="t('catalog_order_form.street.error')"
      >
        <TextInput
          id="street"
          v-model="formState.street"
          :label="t('catalog_order_form.street.label')"
          :has-error-message="!isValidStreet"
          :input-id="inputId"
          :required="true"
          data-test-id="street"
          @change="v.street.$touch()"
          @blur="v.street.$touch()"
        />
      </ValidatedInputGroupLegacy>

      <ValidatedInputGroupLegacy
        v-slot="{ inputId }"
        :is-valid="isValidHouseNo"
        :message="t('catalog_order_form.house_no.error')"
      >
        <TextInput
          id="house-no"
          v-model="formState.houseNo"
          :label="t('catalog_order_form.house_no.label')"
          :has-error-message="!isValidHouseNo"
          :input-id="inputId"
          :required="true"
          data-test-id="house-no"
          @change="v.houseNo.$touch()"
          @blur="v.houseNo.$touch()"
        />
      </ValidatedInputGroupLegacy>
    </div>

    <TextInput
      id="additional-address"
      v-model="formState.additionalAddress"
      class="mb-6"
      :label="t('catalog_order_form.additional_address.label')"
      input-id="additional_address"
      data-test-id="additional-address"
    />

    <div class="mb-4 grid grid-cols-2 gap-4">
      <ValidatedInputGroupLegacy
        v-slot="{ inputId }"
        :is-valid="isValidZipCode"
        :message="t('catalog_order_form.zip.invalid')"
      >
        <TextInput
          id="zip"
          v-model="formState.zip"
          :label="t('catalog_order_form.zip.label')"
          :has-error-message="!isValidZipCode"
          :input-id="inputId"
          :required="true"
          data-test-id="zip"
          @change="v.zip.$touch()"
          @blur="handleZipBlur"
        />
      </ValidatedInputGroupLegacy>

      <ValidatedInputGroupLegacy
        v-slot="{ inputId }"
        :is-valid="isValidCity"
        :message="t('catalog_order_form.city.error')"
      >
        <TextInput
          id="city"
          v-model="formState.city"
          :label="t('catalog_order_form.city.label')"
          :has-error-message="!isValidCity"
          :input-id="inputId"
          :required="true"
          data-test-id="city"
          @change="v.city.$touch()"
          @blur="v.city.$touch()"
        />
      </ValidatedInputGroupLegacy>
    </div>

    <div class="mb-4 grid lg:grid-cols-2">
      <div class="text-sm">
        {{ t('catalog_order_form.hint') }}
      </div>
    </div>

    <Error v-if="error" class="mb-4">
      {{ t('catalog_order_form.sent.error') }}
    </Error>
    <Success v-if="sent" class="mb-4">
      {{ t('catalog_order_form.sent.success') }}
    </Success>

    <p class="mb-4 text-xs text-gray-secondary">
      {{ $t('form_fields.mandatory_field_note') }}
    </p>

    <Button type="primary" :loading="fetching" @click="submit">
      {{ t('catalog_order_form.submit') }}
    </Button>
  </form>
</template>

<script setup lang="ts">
import { required } from '@vuelidate/validators'
import { reactive, ref, computed, defineOptions } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PropType } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import useCountryCheck from '#layers/base/composables/useCountryCheck'
import useCatalogOrderForm from '#layers/base/composables/useCatalogOrderForm'
import {
  validateZipCode,
  formatZipCode,
  zipCodeMap,
} from '#layers/base/utils/zipCodeMapRegex'
import useWatch from '#layers/base/composables/useWatch'
import { useCurrentShop } from '#storefront/composables'
import type { SbCatalogOrderForm } from '~/cms/storyblok/types/storyblok.d.ts'
import { scrollToFirstInputWithError } from '#layers/base/utils/forms'

defineProps({
  blok: {
    type: Object as PropType<SbCatalogOrderForm>,
    required: true,
  },
})

const { t } = useI18n()

const { fetch, fetching, error } = useCatalogOrderForm()
const { isInList: isFinland } = useCountryCheck(['fi'])
const { trackCatalogueOrder } = useTrackingEvents()
const currentShop = useCurrentShop()

const isValidSalutation = (value: string) => ['f', 'm'].includes(value)
const isValidZipFormat = (value: string) =>
  validateZipCode(value, currentShop.value.locale, zipCodeMap)

const formatZip = () => {
  formState.zip = formatZipCode(formState.zip, currentShop.value.locale)
}

const handleZipBlur = () => {
  formatZip()
  v.value.zip.$touch()
}

const formState = reactive({
  salutation: 'f' as 'f' | 'm',
  firstName: '',
  lastName: '',
  street: '',
  houseNo: '',
  additionalAddress: '',
  zip: '',
  city: '',
})

const rules = {
  salutation: { required, isValidSalutation },
  firstName: { required },
  lastName: { required },
  street: { required },
  houseNo: { required },
  zip: { required, isValidZipFormat },
  city: { required },
}

const v = useVuelidate(rules, formState, { $lazy: true })

const sent = ref(false)
const form = ref()

const isValidFirstName = computed(() => !v.value.firstName.$invalid)
const isValidLastName = computed(() => !v.value.lastName.$invalid)
const isValidStreet = computed(() => !v.value.street.$invalid)
const isValidHouseNo = computed(() => !v.value.houseNo.$invalid)
const isValidZipCode = computed(() => !v.value.zip.$invalid)
const isValidCity = computed(() => !v.value.city.$invalid)

const clearForm = async () => {
  formState.salutation = 'f'
  formState.firstName = ''
  formState.lastName = ''
  formState.street = ''
  formState.houseNo = ''
  formState.additionalAddress = ''
  formState.zip = ''
  formState.city = ''
  v.value.$reset()

  await form.value.reset()
}

useWatch(formState, () => {
  if (Object.keys(formState).some((value) => value)) {
    sent.value = false
  }
})

const submit = async () => {
  formatZip()
  if (await v.value.$validate()) {
    fetch({
      locale: currentShop.value.locale,
      title: formState.salutation,
      firstName: formState.firstName,
      name: formState.lastName,
      street: formState.street,
      houseNo: formState.houseNo,
      additionalAddress: formState.additionalAddress,
      zip: formState.zip,
      city: formState.city,
      gRecaptchaResponse: 'NULL',
    })
      .then(() => clearForm())
      // async action is required for watch to take effect before
      .then(() => {
        sent.value = true
        return undefined
      })
      .then(() => trackCatalogueOrder())
      .catch((err) => {
        console.error('Catalog order submission failed:', err)
      })
  } else {
    scrollToFirstInputWithError()
  }
}

defineOptions({ name: 'CatalogOrderForm' })
</script>
